import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { Link } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import UserPhoto from '../../../../components/user-photo/user-photo';
import LoadingOverlay from '../../../../components/loading-ovelay/loading-overlay';
import { GetProfileNew, EditProfileNew } from '../../../../services/api/profile-api';
import { Formik } from 'formik';
import { ToBase64 } from '../../../../services/api/file-api';
import * as yup from 'yup';
import './profile-edit-panel.scss';

const ProfileEditPanelNew = () => {
    const [profileState, setProfileState] = useState({
        profile: {},
        loaded: false,
        passwordType: 'password',
        confPasswordType: 'password',
        ssoEnabled: true
    });

    const [alertState, setAlertState] = useState({
        showAlert: false,
        alertAction: '',
        alertMessage: '',
        alertVariant: ''
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const profileData = (await GetProfileNew()).data;
                setProfileState({ ...profileState, profile: { ...profileData, PasswordConfirmation: profileData.Password }, loaded: true, profileImage: profileData.ProfileImage, ssoEnabled: profileData.Password === '' });
            } catch (error) {
                setAlertState({ showAlert: true, alertAction: 'Could not get profile.', alertMessage: '', alertVariant: 'danger' });
            }
        };

        fetchProfile();
    }, []);

    const editProfile = async (profile) => {
        profile = { ...profile, ProfileImage: profileState.profileImage };
        try {
            await EditProfileNew(profile);
            showAlert('Profile Successfully Edited.', 'success');
        } catch (error) {
            showAlert('Could not edit profile.', 'danger');
        }
    };

    const onProfilePictureUploaded = async (e) => {
        let file = e.target.files[0];
        const validImageTypes = ['image/jpeg', 'image/png', 'image/bmp', 'image/tiff', 'image/gif'];

        if (!validImageTypes.includes(file.type)) {
            showAlert('Invalid Picture Format.', 'danger');
        } else if (file.size > 2000000) {
            showAlert('File size too large. Image must be less than 2MB.', 'danger');
        } else if (file.type === 'image/tiff') {
            // Browsers cannot render TIFF data URLs, so the preview and the
            // saved avatar would both show as a gray placeholder. Convert to a
            // PNG data URL client-side — same shared util as the department
            // logo upload (TAD-5781).
            try {
                const { tiffToPngDataUrls } = await import('../../../../util/tiff-helpers');
                const buffer = await file.arrayBuffer();
                const [dataUrl] = tiffToPngDataUrls(buffer, 1);
                // Re-check the cap: PNG re-encoding can inflate the payload.
                if (dataUrl.slice(dataUrl.indexOf(',') + 1).length * 0.75 > 2000000) {
                    showAlert('File size too large. Image must be less than 2MB.', 'danger');
                    return;
                }
                setProfileState({ ...profileState, profileImageRaw: dataUrl, profileImage: dataUrl.split(',')[1] });
            } catch (error) {
                showAlert('Invalid Picture Format.', 'danger');
            }
        } else {
            ToBase64(file)
                .then((result) => {
                    setProfileState({ ...profileState, profileImageRaw: result, profileImage: result.split(',')[1] });
                })
                .catch((error) => {
                    showAlert('Error uploading image.', 'danger');
                });
        }
    };

    const showAlert = (action, variant) => {
        setAlertState({ showAlert: true, alertAction: action, alertMessage: '', alertVariant: variant });
    };

    const clearImage = (event) => {
        event.preventDefault();
        setProfileState({ ...profileState, profileImage: '', profileImageRaw: '' });
    };

    const showHidePassword = (event, passType) => {
        event.preventDefault();
        passType === 'password' ? setProfileState({ ...profileState, passwordType: profileState.passwordType === 'password' ? 'text' : 'password' }) :
            setProfileState({ ...profileState, confPasswordType: profileState.confPasswordType === 'password' ? 'text' : 'password' });
    };

    const displayAlert = () => {
        setTimeout(() => {
            setAlertState({ ...alertState, showAlert: false });
        }, 5000);

        return (
            <Alert show={alertState.showAlert} onClose={() => setAlertState({ ...alertState, showAlert: false })} variant={alertState.alertVariant} dismissible>
                <Alert.Heading>{alertState.alertAction}</Alert.Heading>
                <p>{alertState.alertMessage}</p>
                <hr />
                <div className="d-flex justify-content-end"></div>
            </Alert>
        );
    };

    return (
        <div className="profile-edit-panel dashboard-container-shadow" data-testid="profile-edit-panel">
            <LoadingOverlay area="profile-edit-panel-area" />
            {alertState.showAlert ? displayAlert() : <></>}
            <Formik
                key={profileState.loaded}
                validationSchema={yup.object({
                    Name: yup.string().required('Username required'),
                    Email: yup.string().required('Username required').email('Enter a valid email'),
                    Password: yup.string().min(6, 'Password must be between 6-26 characters in length').max(26, 'Password must be between 6-26 characters in length').required('Password Required').nullable(),
                    PasswordConfirmation: yup.string().oneOf([yup.ref('Password')], 'Passwords must match').required('Passwords must match').nullable(),
                })}
                initialValues={profileState.profile}
                onSubmit={(values) => { editProfile(values) }}
                validator={() => ({})}
            >
                {({
                    handleSubmit,
                    handleChange,
                    values,
                    touched,
                    errors,
                }) => (
                    <Form onSubmit={handleSubmit} className="create-container" data-testid="profile-form">
                        <div className="left-col">
                            <Form.Row>
                                <Form.Group className="picture">
                                    <UserPhoto key={profileState.profileImage} photoBase64={profileState.profileImageRaw} photo={profileState.profileImage} name={values.Name} className="picture-icon" />
                                    <div className="choose-img-btn-container">
                                        <Button className="btn-wide choose-img-button">
                                            <Form.Control
                                                name="user-photo"
                                                id="user-photo"
                                                label="Upload Profile Image"
                                                className="upload-image-button"
                                                type="file"
                                                onChange={(e) => { onProfilePictureUploaded(e) }}
                                            />
                                            <div className="camera"></div>
                                            <div className="label-spacing">Upload Profile Image</div>
                                        </Button>
                                        <Button className="btn-wide clear choose-img-button" onClick={(e) => { clearImage(e) }}>
                                            <div className="camera-clear"></div>
                                            <div className="label-spacing">Clear Image</div>
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Form.Row>
                            <Form.Row>
                                <Form.Group>
                                    <Form.Label>Full Name</Form.Label>
                                    <Form.Control
                                        id="Name"
                                        name="Name"
                                        value={values.Name}
                                        onChange={(e) => { handleChange(e) }}
                                        placeholder="Enter Name"
                                        isInvalid={touched.Name && errors.Name}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.Name}</Form.Control.Feedback>
                                </Form.Group>
                            </Form.Row>
                            <Form.Row>
                                <Form.Group>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        id="Email"
                                        type="email"
                                        name="Email"
                                        value={values.Email}
                                        onChange={handleChange}
                                        placeholder="Email"
                                        isInvalid={touched.Email && errors.Email}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.Email}</Form.Control.Feedback>
                                </Form.Group>
                            </Form.Row>
                            <Form.Row>
                                <Button type="submit" className="btn-wide btn-submit" variant="primary">Save</Button>
                                <Link to="/profile"><Button className="btn-wide btn-outlined btn" variant="primary">Back</Button></Link>
                            </Form.Row>
                        </div>
                        <div className="right-col">
                            {!profileState.ssoEnabled &&
                                <Form.Group className="password-fields">
                                    <Form.Group>
                                        <Form.Label>Password *</Form.Label>
                                        <Button className="view-icon" onClick={(e) => { showHidePassword(e, 'password') }} />
                                        <Form.Control
                                            autoComplete="off"
                                            type={profileState.passwordType}
                                            name="Password"
                                            id="Password"
                                            placeholder="Password"
                                            value={values.Password}
                                            onChange={handleChange}
                                            isInvalid={touched.Password && errors.Password}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.Password}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>Confirm Password *</Form.Label>
                                        <Button className="view-icon" onClick={(e) => { showHidePassword(e, 'confpassword') }} />
                                        <Form.Control
                                            autoComplete="off"
                                            type={profileState.confPasswordType}
                                            id="PasswordConfirmation"
                                            disabled={values.Password === '' || values.Password === undefined}
                                            placeholder="Confirm Password"
                                            value={values.PasswordConfirmation}
                                            onChange={handleChange}
                                            isInvalid={errors.PasswordConfirmation}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.PasswordConfirmation}</Form.Control.Feedback>
                                    </Form.Group>
                                </Form.Group>
                            }
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default ProfileEditPanelNew;
